import { ReactComponent as BinSVG } from '@assets/SVGs/Colaborador/BinSVG.svg';
import { ReactComponent as SearchSVG } from '@assets/SVGs/PresenteeismTable/SearchSVG.svg';
import { usePopup } from '@contexts/popupContext';
import ClickableWrapper from '@shared/designSystem/ClickableWrapper';
import { ToastManager } from '@shared/designSystem/messages/ToastManager';
import styles from '@styles/components/Configuracion/Novedades/ConfiguracionNovedadesLista.module.css';
import { useEffect, useState } from 'react';
import ResultPopup from '@components/General/ResultPopup';
import { Application } from '@core/Application';
import { CreateNovedadRequest, DisableNovedadRequest, Novedad } from '@services/NovedadesService';
import { getNovedadColors } from '@utils/NovedadDefaultColors';
import NovedadEditModal from './NovedadEditModal';
import { Loader } from '@shared/designSystem/Loader';

interface NovedadItem extends Novedad {
    enabledForLicenses?: boolean;
    enabledForLeaves?: boolean;
    habilitadoParaLicencias?: boolean;
}

const hexColorPattern = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const isValidHexColor = (color?: string) => typeof color === 'string' && hexColorPattern.test(color.trim());

const getColorSampleStyle = (novedad: NovedadItem): React.CSSProperties => {
    const colors = getNovedadColors(novedad);

    return {
        backgroundColor: isValidHexColor(colors?.backgroundColor) ? colors?.backgroundColor : '#F5F4F7',
        color: isValidHexColor(colors?.foregroundColor) ? colors?.foregroundColor : '#667085',
        borderColor: isValidHexColor(colors?.backgroundColor) ? colors?.backgroundColor : '#DDDFE3',
    };
};


const ConfiguracionNovedadesLista = ({orgID}) => {

    const defaultNovedades = [
        {name: 'Presente', acronym: 'P'}, 
        {name: 'Presente - Tarde', acronym: 'P-T'}, 
        {name: 'Presente - Salida temprana', acronym: 'P-ST'}, 
        {name: 'Dia Franco', acronym: 'FR'}, 
        {name: 'Ausente', acronym: 'AU'}, 
        {name: 'Feriado', acronym: 'FE'},
        {name: 'Vacaciones', acronym: 'VAC', isUsableInLicenses: true},
        {name: 'Enfermedad', acronym: 'EN', isUsableInLicenses: true},
        {name: 'Desactivado', acronym: 'DES'},
        {name: 'Compensatorio', acronym: 'C'},
    ]

    const {showPopup} = usePopup()

    const [acronym, setAcronym] = useState("")
    const [description, setDescription] = useState("")
    const [searchField, setSearchField] = useState("")

    const [novedades, setNovedades] = useState<NovedadItem[]>([])
    const [loading, setLoading] = useState(false)
    const [updatingAcronym, setUpdatingAcronym] = useState<string | null>(null)
    const [editingNovedad, setEditingNovedad] = useState<NovedadItem | null>(null)

    useEffect(() => {
        void fetchNovedades()
    }, [orgID])

    const fetchNovedades = async () => {
        setLoading(true)
        try {
            const novedadesService = Application.shared().coreDependencies.novedadesService;
            const json = await novedadesService.getNovedades(orgID);
            setNovedades(json)
        } catch (error) {
            console.error(error)
        }
        setLoading(false)
    }

    const handleAddNovedad = async () => {
        if (acronym === "" || description === "") return
        const filteredNovedades = novedades.filter(novedad => novedad.acronym === acronym)
        if (filteredNovedades.length > 0) return

        await addNovedad()
        await fetchNovedades()
    }

    const isNovedadSubmittable = () => {
        if (acronym === "" || description === "") return false
        if (acronym.includes("/")) return false

        const filteredNovedades = novedades.filter(novedad => novedad.acronym === acronym)
        if (filteredNovedades.length > 0) return false

        return true
    }

    const addNovedad = async () => {
        const data: CreateNovedadRequest = {
            novedad: description,
            novedadAcronym: acronym
        };
        
        try {
            const novedadesService = Application.shared().coreDependencies.novedadesService;
            await novedadesService.createNovedad(orgID, data);
            showPopup(() => <ResultPopup result={"Novedad agregada!"} succesful={true}/>)

        } catch (error) {
            showPopup(() => <ResultPopup result={"Error al agregar novedad..."} succesful={false}/>)
            console.error(error)
        }
    }
    

    const handleDeleteNovedad = async (novedad) => {
        await deleteNovedad(novedad.acronym, novedad.name)
        await fetchNovedades()
    }

    const deleteNovedad = async (acronym, name) => {
        const data: DisableNovedadRequest = {
            novedadAcronym: acronym
        };
        
        try {
            const novedadesService = Application.shared().coreDependencies.novedadesService;
            await novedadesService.disableNovedad(orgID, data);
            showPopup(() => <ResultPopup result={"Novedad eliminada!"} succesful={true}/>)

        } catch (error) {
            showPopup(() => <ResultPopup result={"Error al eliminar novedad..."} succesful={false}/>)
            console.error(error)
        }
    }

    const isDefaultNovedad = (novedad: NovedadItem) => {
        return defaultNovedades.some(defaultNovedad =>
            defaultNovedad.acronym === novedad.acronym
        )
    }

    const isNovedadEnabledForLicenses = (novedad: NovedadItem) => {
        return Boolean(
            novedad.isUsableInLicenses ??
            novedad.habilitadoParaLicencias ??
            novedad.enabledForLicenses ??
            novedad.enabledForLeaves
        )
    }

    const handleSaveEditedNovedad = async (
        novedad: NovedadItem,
        payload: CreateNovedadRequest,
        isUsableInLicenses: boolean,
    ) => {
        const novedadesService = Application.shared().coreDependencies.novedadesService;
        const previousLicenseUsability = isNovedadEnabledForLicenses(novedad);

        setUpdatingAcronym(novedad.acronym);

        try {
            await novedadesService.createNovedad(orgID, payload);

            if (previousLicenseUsability !== isUsableInLicenses) {
                await novedadesService.setNovedadLicenseEnabled(orgID, {
                    novedadAcronym: novedad.acronym,
                    isUsableInLicenses,
                });
            }

            ToastManager.shared().showSuccessMessage("Novedad actualizada!".localized());
            setEditingNovedad(null);
            await fetchNovedades();
        } catch (error) {
            ToastManager.shared().showErrorMessage("Error al actualizar la novedad...".localized());
            console.error(error);
        } finally {
            setUpdatingAcronym(null);
        }
    }

    return (
    <div className={styles['content-block']}>
        <div className={styles['header']}>
            <div style={{display: "flex", flexDirection: "row", marginBottom: "35px"}}>
                <div className={styles['svg-container']} style={{marginTop: '5px', marginRight: '15px'}}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 20 20" fill="none">
                        <path d="M13.8325 6.17463L8.10904 11.9592L1.59944 7.88767C0.66675 7.30414 0.860765 5.88744 1.91572 5.57893L17.3712 1.05277C18.3373 0.769629 19.2326 1.67283 18.9456 2.642L14.3731 18.0868C14.0598 19.1432 12.6512 19.332 12.0732 18.3953L8.10601 11.9602" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h2 className={styles['title-nomina']}>
                    Carga de novedades
                </h2>
            </div>

            <p className={styles["title-subtext"]} style={{marginBottom: "20px"}}>Definir las Novedades de tu Organización asignando un código de hasta 4 letras y una descripción:</p>
        </div> 

        <div className={styles["actions-container"]} >
                <div style={{display: "flex", flexDirection: "row", gap: "25px", alignItems: "center"}}>
                    <div style={{width: "100px"}}>
                        <p className={styles['edit-grid-input-label']} style={{marginBottom: '5px'}}>Código</p>
                        <div className={styles['edit-grid-input-container']}>
                            <input 
                            type='text' 
                            placeholder='Codigo...' 
                            className={styles['edit-grid-input']}
                            onChange={(e) => setAcronym(e.target.value)}
                            value={acronym}
                            />
                        </div>
                        
                    </div>

                    <div style={{width: "300px"}}>
                        <p className={styles['edit-grid-input-label']} style={{marginBottom: '5px'}}>Descripción</p>
                        <div className={styles['edit-grid-input-container']}>
                            <input 
                            type='text' 
                            placeholder='Descripcion...' 
                            className={styles['edit-grid-input']}
                            onChange={(e) => setDescription(e.target.value)}
                            value={description}
                            />
                        </div>
                        
                    </div>


                    <div style={{width: "32px", height: "32px", marginTop: "15px"}}>
                        {isNovedadSubmittable() ?
                            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 32 32" fill="none" style={{cursor: "pointer"}} onClick={handleAddNovedad}>
                                <circle cx="16" cy="16" r="15.25" stroke="#EC6C56" stroke-width="1.5"/>
                                <path d="M22.0122 11.7344L13.4789 20.2677L9.6001 16.3889" stroke="#EC6C56" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        :
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                                <circle cx="16" cy="16" r="15.25" stroke="#DDDDDD" stroke-width="1.5"/>
                                <path d="M22.0121 11.7344L13.4788 20.2677L9.60001 16.3889" stroke="#DDDDDD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        }
                    </div>
                </div>

                <div className={styles['search-bar']} style={{marginTop: "15px"}}>
                    <div className={styles['svg-container']}>
                        <SearchSVG/>
                    </div>

                    <div className={styles['search-field-container']}>
                        <input type='text' placeholder='Buscar...' className={styles['search-input']} onChange={(e) => setSearchField(e.target.value)}/>
                    </div>
                </div>
        </div>


        <div className={styles["novedades-container"]} style={novedades && {backgroundColor: "white"}}>
        {!loading ? (
            (() => {
                const filteredNovedades = novedades.filter(novedad => {
                    return novedad.name.toLowerCase().replace(/\s/g, '').includes(searchField.toLowerCase().replace(/\s/g, ''));
                }).filter(novedad => novedad.disabled === false || novedad.disabled === undefined);

                const novedadesHeader = (
                    <div className={styles['novedades-header']}>
                        <div className={styles['novedad-info']}>
                            <p className={styles['novedades-header-label']}>Código</p>
                            <p className={styles['novedades-header-label']}>Descripción</p>
                        </div>
                        <div className={styles['novedad-payroll-column']}>
                            <p className={styles['novedades-header-label']}>Código nómina</p>
                        </div>
                        <div className={styles['novedad-color-column']}>
                            <p className={styles['novedades-header-label']}>Color</p>
                        </div>
                        <div className={styles['novedades-license-column']}>
                            <p className={styles['novedades-header-label']}>Habilitado para WhatsApp</p>
                        </div>
                        <div className={styles['novedad-actions']} />
                    </div>
                );

                return filteredNovedades.length > 0 ? (
                    <>
                        {novedadesHeader}
                        <div className={styles['novedades-scroll-content']}>
                            {filteredNovedades.map((novedad, index) => (
                                <div
                                    className={styles['novedad-container']}
                                    style={index % 2 === 0 ? { background: '#FBFBFB' } : { background: '#FFF' }}
                                    key={novedad.acronym}
                                >
                                    <ClickableWrapper
                                        className={styles['novedad-clickable-area']}
                                        onClick={() => setEditingNovedad(novedad)}
                                        disabled={updatingAcronym === novedad.acronym}
                                        title={"Editar novedad".localized()}
                                    >
                                        <div className={styles['novedad-info']}>
                                            <p className={styles['novedad-info-acronym']}>{novedad.acronym}</p>
                                            <p className={styles['novedad-info-description']}>{novedad.name}</p>
                                        </div>
                                        <div className={styles['novedad-payroll-column']}>
                                            <p className={styles['novedad-payroll-code']}>{novedad.payrollCode || '-'}</p>
                                        </div>
                                        <div className={styles['novedad-color-column']}>
                                            {getNovedadColors(novedad) ? (
                                                <div
                                                    className={styles['novedad-color-sample']}
                                                    style={getColorSampleStyle(novedad)}
                                                    title={`${getNovedadColors(novedad)?.backgroundColor || '-'} / ${getNovedadColors(novedad)?.foregroundColor || '-'}`}
                                                >
                                                    {novedad.acronym}
                                                </div>
                                            ) : (
                                                <p className={styles['novedad-color-empty']}>-</p>
                                            )}
                                        </div>
                                        <div className={styles['novedades-license-column']}>
                                            <input
                                                type="checkbox"
                                                checked={isNovedadEnabledForLicenses(novedad)}
                                                readOnly={true}
                                                tabIndex={-1}
                                                style={{ pointerEvents: 'none' }}
                                            />
                                        </div>
                                    </ClickableWrapper>
                                    <div className={styles['novedad-actions']}>
                                        {!isDefaultNovedad(novedad) && (
                                            <div className={styles['svg-container']} onClick={() => handleDeleteNovedad(novedad)}>
                                                <BinSVG />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className={styles['no-qontact-points']}>
                        {novedadesHeader}
                        <p>No se han encontrado novedades!</p>
                    </div>
                );
            })()
        ) : (
            <div className={styles['no-qontact-points']}>
                <Loader height="48px" width="48px" />
            </div>
        )}
        </div>
        <NovedadEditModal
            novedad={editingNovedad}
            isOpen={editingNovedad !== null}
            loading={updatingAcronym === editingNovedad?.acronym}
            onClose={() => {
                if (updatingAcronym) {
                    return;
                }
                setEditingNovedad(null);
            }}
            onSave={handleSaveEditedNovedad}
        />
    </div>
    );
};

export default ConfiguracionNovedadesLista
